(() => {
  if (window.AlawooderImageViewer) {
    window.AlawooderImageViewer.init(document);
    return;
  }

  const mounted = new Map();
  const rootsWithin = scope => [
    ...(scope.matches?.('[data-aw-product]') ? [scope] : []),
    ...scope.querySelectorAll('[data-aw-product]')
  ];

  function mount(root) {
    if (mounted.has(root)) return;
    const dialog = root.querySelector('[data-aw-image-viewer]');
    const track = root.querySelector('[data-aw-gallery-track]');
    if (!dialog || !track) return;
    const stage = dialog.querySelector('[data-aw-viewer-stage]');
    const thumbRail = dialog.querySelector('[data-aw-viewer-thumbs]');
    const counter = dialog.querySelector('[data-aw-viewer-count]');
    const caption = dialog.querySelector('[data-aw-viewer-caption]');
    const previous = dialog.querySelector('[data-aw-viewer-prev]');
    const next = dialog.querySelector('[data-aw-viewer-next]');
    const closeButton = dialog.querySelector('[data-aw-viewer-close]');
    const controller = new AbortController();
    const listen = (node, type, handler) => node.addEventListener(type, handler, {signal: controller.signal});
    let records = [];
    let index = 0;
    let lastTrigger = null;
    let viewerThumbs = [];

    const pause = scope => scope.querySelectorAll('video, audio').forEach(media => media.pause());
    const cleanClone = node => {
      const clone = node.cloneNode(true);
      [clone, ...clone.querySelectorAll('[id], [autoplay]')].forEach(element => {
        element.removeAttribute('id');
        element.removeAttribute('autoplay');
      });
      if (clone.matches('img')) {
        clone.loading = 'eager';
        clone.decoding = 'async';
        clone.sizes = '100vw';
      } else if (clone.matches('video')) {
        clone.controls = true;
        clone.autoplay = false;
        clone.preload = 'metadata';
      } else if (clone.matches('iframe')) {
        // Keep the existing embed and its permissions; never autoplay a duplicate.
        try {
          const source = new URL(clone.src, location.href);
          source.searchParams.set('autoplay', '0');
          clone.src = source.href;
        } catch (_) { /* An existing embed may use a non-URL source. */ }
        clone.title = clone.title || 'Product video';
      }
      return clone;
    };

    function select(destination, updateNative = true) {
      if (!records.length) return;
      index = Math.max(0, Math.min(records.length - 1, destination));
      const record = records[index];
      pause(stage);
      stage.replaceChildren();
      const media = record.slide.querySelector('video, iframe, model-viewer, img, svg');
      if (media) stage.appendChild(cleanClone(media));
      else {
        const unavailable = document.createElement('p');
        unavailable.textContent = 'No image available.';
        stage.appendChild(unavailable);
      }
      counter.textContent = `${String(index + 1).padStart(2, '0')} / ${String(records.length).padStart(2, '0')}`;
      caption.textContent = record.caption;
      previous.disabled = index === 0;
      next.disabled = index === records.length - 1;
      viewerThumbs.forEach((thumb, current) => thumb.setAttribute('aria-current', String(current === index)));
      const selected = viewerThumbs[index];
      if (selected) {
        const item = selected.getBoundingClientRect();
        const rail = thumbRail.getBoundingClientRect();
        if (item.left < rail.left || item.right > rail.right) {
          thumbRail.scrollTo({left: Math.max(0, thumbRail.scrollLeft + item.left - rail.left - (rail.width - item.width) / 2), behavior: 'instant'});
        }
      }
      // Product.js remains the single owner of the actual gallery selection.
      if (updateNative) root.dispatchEvent(new CustomEvent('aw:gallery:select', {detail: {mediaId: record.slide.dataset.awMedia, instant: true}}));
    }

    function close() {
      if (!dialog.open) return;
      if (typeof dialog.close === 'function') dialog.close();
      else {
        dialog.removeAttribute('open');
      }
      afterClose();
    }

    function afterClose() {
      pause(stage);
      stage.replaceChildren();
      thumbRail.replaceChildren();
      document.body.classList.remove('aw-image-viewer-open');
      records = [];
      viewerThumbs = [];
      if (lastTrigger?.isConnected) lastTrigger.focus({preventScroll: true});
      lastTrigger = null;
    }

    function open(trigger) {
      if (dialog.open) return;
      const nativeThumbs = new Map([...root.querySelectorAll('[data-aw-thumb]')].map(thumb => [thumb.dataset.awThumb, thumb]));
      records = [...track.querySelectorAll('[data-aw-media]')].map(slide => ({
        slide,
        nativeThumb: nativeThumbs.get(slide.dataset.awMedia),
        caption: slide.dataset.awCaption || slide.querySelector('img')?.alt || ''
      }));
      if (!records.length) return;
      lastTrigger = trigger;
      const current = records.findIndex(record => record.nativeThumb?.getAttribute('aria-current') === 'true');
      viewerThumbs = records.map((record, currentIndex) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'aw-image-viewer-thumb';
        button.dataset.awViewerIndex = String(currentIndex);
        button.setAttribute('aria-label', `View image ${currentIndex + 1}${record.caption ? `: ${record.caption}` : ''}`);
        const preview = record.nativeThumb?.querySelector('img') || record.slide.querySelector('img');
        if (preview) {
          const image = cleanClone(preview);
          image.alt = '';
          image.loading = 'eager';
          image.sizes = '74px';
          button.appendChild(image);
        } else button.textContent = String(currentIndex + 1).padStart(2, '0');
        if (record.slide.querySelector('video, iframe, model-viewer')) {
          const play = document.createElement('span');
          play.className = 'aw-image-viewer-thumb-play';
          play.setAttribute('aria-hidden', 'true');
          play.textContent = record.slide.querySelector('model-viewer') ? '◇' : '▷';
          button.appendChild(play);
        }
        return button;
      });
      thumbRail.replaceChildren(...viewerThumbs);
      pause(track);
      document.body.classList.add('aw-image-viewer-open');
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else {
        dialog.setAttribute('open', '');
        dialog.setAttribute('aria-modal', 'true');
      }
      select(current < 0 ? 0 : current, true);
      closeButton.focus({preventScroll: true});
    }

    listen(root, 'click', event => {
      const trigger = event.target.closest('[data-aw-gallery-expand]');
      if (!trigger || !root.contains(trigger)) return;
      event.preventDefault();
      open(trigger);
    });
    listen(closeButton, 'click', close);
    listen(previous, 'click', () => select(index - 1));
    listen(next, 'click', () => select(index + 1));
    listen(thumbRail, 'click', event => {
      const thumb = event.target.closest('[data-aw-viewer-index]');
      if (thumb) select(Number(thumb.dataset.awViewerIndex));
    });
    listen(dialog, 'cancel', event => { event.preventDefault(); close(); });
    listen(dialog, 'close', () => { if (!dialog.open) afterClose(); });
    listen(dialog, 'keydown', event => {
      if (event.key === 'Escape') { event.preventDefault(); close(); return; }
      if (event.target.closest('video, audio, iframe, model-viewer, input, select, textarea')) return;
      const destinations = {ArrowLeft: index - 1, ArrowRight: index + 1, Home: 0, End: records.length - 1};
      if (Object.prototype.hasOwnProperty.call(destinations, event.key)) {
        event.preventDefault();
        event.stopPropagation();
        select(destinations[event.key]);
      }
    });
    mounted.set(root, () => {
      close();
      controller.abort();
      mounted.delete(root);
    });
  }

  function init(scope) { rootsWithin(scope).forEach(mount); }
  function destroy(scope) { rootsWithin(scope).forEach(root => mounted.get(root)?.()); }
  window.AlawooderImageViewer = {init, destroy};
  init(document);
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => destroy(event.target));
})();
